"use client";

import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, Search, FilePen, ShieldCheck, Key } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const milestones = [{
  title: "Consultation",
  description: "We begin with a deep dive into your needs, budget, and lifestyle. This ensures every step we take is aligned with your ultimate goals.",
  icon: MessageSquare
}, {
  title: "Search",
  description: "Curated listings and private tours of Orange County's finest properties. We filter the noise to find homes that truly match your criteria.",
  icon: Search
}, {
  title: "Offer",
  description: "Strategic negotiation backed by local market data. We craft compelling offers to secure your dream home at the best possible value.",
  icon: FilePen
}, {
  title: "Inspection",
  description: "Thorough due diligence with trusted professionals. We guide you through reports and negotiate repairs to protect your investment.",
  icon: ShieldCheck
}, {
  title: "Closing",
  description: "Seamless coordination of paperwork and final details. We ensure a smooth finish so you can celebrate receiving your new keys.",
  icon: Key
}];
export default function TheRoadToClosing() {
  const sectionRef = useRef<HTMLElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const section = sectionRef.current;
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const startOffset = sectionTop - windowHeight / 2;
      const endOffset = sectionTop + sectionHeight - windowHeight / 2;
      const totalDistance = endOffset - startOffset;
      const currentPosition = scrollY - startOffset;
      let progress = currentPosition / totalDistance * 100;
      progress = Math.max(0, Math.min(100, progress));
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="the-road-to-closing" ref={sectionRef} className="dark text-foreground relative py-24 overflow-hidden !bg-[hsl(0,0%,0%)]">
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-medium text-primary mb-6">
            The Road to Closing
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            A transparent, step-by-step journey from our first meeting to your new front door.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px md:-translate-x-1/2 border-l-2 border-dashed border-primary/40 h-full" />

          {}
          <div className="space-y-12 md:space-y-24 relative z-10">
            {milestones.map((item, i) => <MilestoneCard key={i} item={item} index={i} />)}
          </div>
        </div>

        {}
        <div className="fixed right-4 top-1/2 -translate-y-1/2 z-50 hidden xl:flex flex-col items-center gap-4">
          <span className="text-xs font-mono text-primary/70 rotate-90 whitespace-nowrap mb-8">
            PROCESS
          </span>
          {}
          <div className="h-48 w-2 relative flex items-center justify-center">
             <div className="-rotate-90 w-48 absolute">
                <Progress value={scrollProgress} className="h-1.5 bg-primary/20" />
             </div>
          </div>
          <span className="text-xs font-mono text-primary/70 mt-8">
            {Math.round(scrollProgress)}%
          </span>
        </div>
      </div>
    </section>;
}
function MilestoneCard({
  item,
  index
}: {
  item: {
    title: string;
    description: string;
    icon: React.ElementType;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref as React.RefObject<HTMLDivElement>} data-index={index} className={cn("relative flex flex-col md:flex-row items-center md:justify-between gap-8 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0 translate-x-0" : `opacity-0 translate-y-12 ${isEven ? "md:-translate-x-12" : "md:translate-x-12"}`)}>
      {}
      <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-background border-2 border-primary rounded-full md:-translate-x-1/2 z-20 mt-6 md:mt-0" />

      {}
      <div className={cn("w-full md:w-[45%] pl-12 md:pl-0", isEven ? "md:text-right md:pr-12" : "md:order-2 md:pl-12")}>
        <Card className="bg-card/50 backdrop-blur-sm border-primary/10 hover:border-primary/30 transition-colors duration-300">
          <CardHeader className={cn("pb-2", isEven ? "md:items-end" : "")}>
            <div className="p-3 bg-primary/10 w-fit rounded-lg mb-2 text-primary">
              <item.icon className="w-6 h-6" />
            </div>
            <CardTitle className="text-xl md:text-2xl text-primary">
              {item.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-base text-muted-foreground leading-relaxed">
              {item.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="hidden md:block w-[45%]" />
    </div>;
}